import { useState, useEffect, useMemo } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import Style from './index.module.css';
import { EnhetViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import { format, addDays, isBefore } from 'date-fns';
import CommonLoader from 'components/CommonLoader';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';

type ActionType = 'fjern' | 'rediger';

interface Props {
  onFjernVidereforing: () => void;
  onUpdateVidereforingDato: (nyNedlagtDato: Date) => void;
  show?: boolean;
  handleClose: () => void;
  isLoading: boolean;
  enhet: EnhetViewModel;
  successorEnhet?: EnhetViewModel;
}

const VidereforingModal = (props: Props) => {
  const [action, setAction] = useState<ActionType>('rediger');
  const [nyNedlagtDato, setNyNedlagtDato] = useState<Date | undefined>(undefined);

  // Initialize date when modal opens or enhet changes
  useEffect(() => {
    if (props.show && props.enhet?.firma?.nedlagtDato) {
      setNyNedlagtDato(new Date(props.enhet.firma.nedlagtDato));
      setAction('rediger');
    }
  }, [props.show, props.enhet?.firma?.nedlagtDato]);

  const handleSubmit = () => {
    if (action === 'fjern') {
      props.onFjernVidereforing();
    } else if (action === 'rediger' && nyNedlagtDato) {
      props.onUpdateVidereforingDato(nyNedlagtDato);
    }
  };

  const currentNedlagtDato = props.enhet?.firma?.nedlagtDato
    ? new Date(props.enhet.firma.nedlagtDato)
    : undefined;

  const hasDateChanged =
    currentNedlagtDato &&
    nyNedlagtDato &&
    format(currentNedlagtDato, 'yyyy-MM-dd') !== format(nyNedlagtDato, 'yyyy-MM-dd');

  const isSubmitDisabled =
    props.isLoading || (action === 'rediger' && (!nyNedlagtDato || !hasDateChanged));

  const nesteOppstartDato = nyNedlagtDato ? addDays(nyNedlagtDato, 1) : undefined;

  // Calculate which successor dates will be affected
  const affectedSuccessorDates = useMemo(() => {
    if (!nesteOppstartDato || !props.successorEnhet || !hasDateChanged) {
      return { oppstartsdato: false, regionalKjede: null, markedsKjede: null, grupper: [] };
    }

    const successor = props.successorEnhet;
    const result = {
      oppstartsdato:
        successor.oppstartsdato && isBefore(new Date(successor.oppstartsdato), nesteOppstartDato),
      regionalKjede:
        successor.sisteRegionalkjedeMedlemskap?.fraOgMedDato &&
        isBefore(new Date(successor.sisteRegionalkjedeMedlemskap.fraOgMedDato), nesteOppstartDato)
          ? {
              name: successor.sisteRegionalkjedeMedlemskap.name,
              fraOgMedDato: new Date(successor.sisteRegionalkjedeMedlemskap.fraOgMedDato),
            }
          : null,
      markedsKjede:
        successor.sisteMarkedskjedeMedlemskap?.fraOgMedDato &&
        isBefore(new Date(successor.sisteMarkedskjedeMedlemskap.fraOgMedDato), nesteOppstartDato)
          ? {
              name: successor.sisteMarkedskjedeMedlemskap.name,
              fraOgMedDato: new Date(successor.sisteMarkedskjedeMedlemskap.fraOgMedDato),
            }
          : null,
      grupper: (successor.grupper || [])
        .filter((g) => g.fraOgMedDato && isBefore(new Date(g.fraOgMedDato), nesteOppstartDato))
        .map((g) => ({ name: g.enhetsgrupperingnavn, fraOgMedDato: new Date(g.fraOgMedDato) })),
    };

    return result;
  }, [nesteOppstartDato, props.successorEnhet, hasDateChanged]);

  const hasAffectedDates =
    affectedSuccessorDates.oppstartsdato ||
    affectedSuccessorDates.regionalKjede ||
    affectedSuccessorDates.markedsKjede ||
    affectedSuccessorDates.grupper.length > 0;

  return (
    <Modal show={props.show} onHide={props.handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Rediger videreføring</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          Enheten ble videreført til løpenummer <strong>{props.enhet?.firma.nesteLopenr}</strong>{' '}
          den {currentNedlagtDato ? format(currentNedlagtDato, 'dd.MM.yyyy') : ''}
        </p>

        <Form.Group className="mb-3">
          <Form.Check
            type="radio"
            id="action-rediger"
            name="videreforing-action"
            label="Endre videreføringsdato"
            checked={action === 'rediger'}
            onChange={() => setAction('rediger')}
          />
          <Form.Check
            type="radio"
            id="action-fjern"
            name="videreforing-action"
            label="Fjern videreføring helt"
            checked={action === 'fjern'}
            onChange={() => setAction('fjern')}
          />
        </Form.Group>

        {action === 'rediger' && (
          <Form.Group className="mb-3">
            <Form.Label>Ny nedlagtdato (neste enhet får oppstart dagen etter)</Form.Label>
            <NorwegianDatepicker
              id="ny-nedlagt-dato"
              value={nyNedlagtDato}
              onChange={(date) => setNyNedlagtDato(date)}
            />
            {nesteOppstartDato && hasDateChanged && (
              <>
                <Form.Text className="text-muted d-block mt-2">
                  Neste enhet (løpenr {props.enhet?.firma.nesteLopenr}) vil få ny oppstartsdato:{' '}
                  <strong>{format(nesteOppstartDato, 'dd.MM.yyyy')}</strong>
                </Form.Text>

                {hasAffectedDates && (
                  <div className="alert alert-warning mt-3 mb-0">
                    <strong>Følgende datoer på neste enhet vil bli justert:</strong>
                    <ul className="mb-0 mt-2">
                      {affectedSuccessorDates.oppstartsdato && (
                        <li>
                          Oppstartsdato:{' '}
                          {props.successorEnhet?.oppstartsdato &&
                            format(new Date(props.successorEnhet.oppstartsdato), 'dd.MM.yyyy')}{' '}
                          → <strong>{format(nesteOppstartDato, 'dd.MM.yyyy')}</strong>
                        </li>
                      )}
                      {affectedSuccessorDates.regionalKjede && (
                        <li>
                          Regionalkjede "{affectedSuccessorDates.regionalKjede.name}" innmelding:{' '}
                          {format(affectedSuccessorDates.regionalKjede.fraOgMedDato, 'dd.MM.yyyy')}{' '}
                          → <strong>{format(nesteOppstartDato, 'dd.MM.yyyy')}</strong>
                        </li>
                      )}
                      {affectedSuccessorDates.markedsKjede && (
                        <li>
                          Markedskjede "{affectedSuccessorDates.markedsKjede.name}" innmelding:{' '}
                          {format(affectedSuccessorDates.markedsKjede.fraOgMedDato, 'dd.MM.yyyy')} →{' '}
                          <strong>{format(nesteOppstartDato, 'dd.MM.yyyy')}</strong>
                        </li>
                      )}
                      {affectedSuccessorDates.grupper.map((g, idx) => (
                        <li key={idx}>
                          Enhetsgruppe "{g.name}" innmelding: {format(g.fraOgMedDato, 'dd.MM.yyyy')}{' '}
                          → <strong>{format(nesteOppstartDato, 'dd.MM.yyyy')}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!hasAffectedDates && (
                  <div className="alert alert-info mt-3 mb-0">
                    Ingen datoer på neste enhet trenger justering.
                  </div>
                )}
              </>
            )}
          </Form.Group>
        )}

        {action === 'fjern' && (
          <div className="alert alert-warning">
            Dette vil fjerne videreføringen helt, og koblingen til neste enhet fjernes. Enheten
            forblir nedlagt. Bruk «Reaktiver» hvis den skal være aktiv igjen.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={props.handleClose}>
          Avbryt
        </Button>

        <Button
          className={Style.spinnerBtn}
          variant={action === 'fjern' ? 'danger' : 'primary'}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          <span>{action === 'fjern' ? 'Fjern videreføring' : 'Oppdater dato'}</span>
          {props.isLoading && (
            <span className="ms-2">
              <CommonLoader />
            </span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VidereforingModal;
